import { useContext, useMemo, useState } from "react";
import { Table, Button, Modal, Form, Input, Select, Switch, message } from "antd";
import { PlusOutlined, SearchOutlined } from "@ant-design/icons";
import { useUsers, useCreateUser, useUpdateUser, useDeleteUser, CreateUserPayload } from "../../queries/users";
import { useProjects } from "../../queries/projects";
import { SessionUser, ProjectProps } from "../../types/interfaces";
import { ApiError } from "../../hooks/apiRequest";
import { DisplayComponent } from "../../navigation/layout/DisplayComponent";
import { PageShell, Toolbar } from "../../components/ui/PageShell";
import { StatusTag, RoleTag } from "../../components/ui/StatusTag";
import { EmptyState } from "../../components/ui/States";
import { NoAccess } from "../../components/ui/NoAccess";
import AuthContext from "../../context/AuthContext";

const ROLES: SessionUser["role"][] = ["manager", "agent", "requester"];
const ROLE_LABEL: Record<SessionUser["role"], string> = {
  manager: "Manager", agent: "Agent", requester: "Requester",
};

export const UserListPage = () => {
  const { userData } = useContext(AuthContext);
  const isManager = userData?.role === "manager";

  const { data: users, isLoading, isError, error } = useUsers({ enabled: isManager });
  const { data: projects } = useProjects({ enabled: isManager });
  const createUser = useCreateUser();
  const updateUser = useUpdateUser();
  const deleteUser = useDeleteUser();

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingUser, setEditingUser] = useState<SessionUser | null>(null);
  const [query, setQuery] = useState("");
  const [roleFilter, setRoleFilter] = useState<SessionUser["role"] | undefined>();
  const [form] = Form.useForm();
  const authProviderValue = Form.useWatch("authProvider", form) ?? "local";
  const isMicrosoftUser = authProviderValue === "microsoft";

  const rows = useMemo(() => {
    const q = query.trim().toLowerCase();
    return (users || [])
      .filter((u: SessionUser) => (roleFilter ? u.role === roleFilter : true))
      .filter((u: SessionUser) => (q ? (u.name + " " + u.username).toLowerCase().includes(q) : true));
  }, [users, query, roleFilter]);

  const openCreateModal = () => { setEditingUser(null); form.resetFields(); form.setFieldsValue({ authProvider: "local" }); setIsModalOpen(true); };
  const openEditModal = (user: SessionUser) => {
    form.resetFields(); setEditingUser(user); form.setFieldsValue(user); setIsModalOpen(true);
  };

  const handleSubmit = async () => {
    let values;
    try {
      values = await form.validateFields();
    } catch {
      return; // inline validation messages are already shown
    }
    try {
      if (editingUser) await updateUser.mutateAsync({ id: editingUser._id, data: values });
      else await createUser.mutateAsync(values as CreateUserPayload);
      setIsModalOpen(false);
    } catch (err) {
      message.error(err instanceof ApiError ? err.message : "Could not save the user");
    }
  };

  const handleDelete = (userId: string) => {
    Modal.confirm({
      title: "Delete this user?",
      content: "They lose access immediately. Their logged time stays in reports.",
      okText: "Delete user",
      okButtonProps: { danger: true },
      onOk: async () => {
        try { await deleteUser.mutateAsync(userId); }
        catch (err) { message.error(err instanceof ApiError ? err.message : "Could not delete the user"); }
      },
    });
  };

  if (!isManager) return <NoAccess what="user administration" />;

  const activeCount = (users || []).filter((u: SessionUser) => u.active).length;

  return (
    <PageShell
      kind="list"
      kicker="Administration"
      title="Users"
      meta={[
        { label: "Total", value: <span className="osi-num">{(users || []).length}</span> },
        { label: "Active", value: <span className="osi-num">{activeCount}</span> },
      ]}
      actions={<Button type="primary" icon={<PlusOutlined />} onClick={openCreateModal}>New user</Button>}
    >
      <Toolbar count={rows.length} unit="users">
        <Input
          allowClear
          prefix={<SearchOutlined style={{ color: "var(--osi-text-faint)" }} />}
          placeholder="Search name or username"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          style={{ width: 240 }}
        />
        <Select
          allowClear
          style={{ width: 180 }}
          placeholder="All roles"
          value={roleFilter}
          onChange={setRoleFilter}
          options={ROLES.map((r) => ({ label: ROLE_LABEL[r], value: r }))}
        />
      </Toolbar>

      <DisplayComponent error={isError ? error : undefined} isLoading={isLoading}>
        <Table<SessionUser>
          rowKey="_id"
          size="small"
          dataSource={rows}
          pagination={rows.length > 25 ? { pageSize: 25, size: "small" } : false}
          locale={{ emptyText: <EmptyState title="No users match" description="Clear the filters to see everyone." /> }}
          columns={[
            {
              title: "Name", dataIndex: "name",
              render: (name: string, r) => (
                <span>
                  <span style={{ fontWeight: 500 }}>{name}</span>
                  {r._id === userData?._id && <span style={{ color: "var(--osi-text-faint)", marginLeft: 6 }}>you</span>}
                </span>
              ),
            },
            {
              title: "Username", dataIndex: "username", width: 180, responsive: ["lg"],
              render: (v) => <span style={{ color: "var(--osi-text-muted)" }}>{v}</span>,
            },
            { title: "Role", dataIndex: "role", width: 130, render: (role: SessionUser["role"]) => <RoleTag role={role} /> },
            {
              title: "Sign-in", dataIndex: "authProvider", width: 110, responsive: ["md"],
              render: (provider?: SessionUser["authProvider"]) => <StatusTag label={provider === "microsoft" ? "Microsoft" : "Local"} tone="neutral" />,
            },
            {
              title: "Status", dataIndex: "active", width: 120,
              render: (active: boolean) => <StatusTag label={active ? "Active" : "Inactive"} tone={active ? "ok" : "neutral"} dot />,
            },
            {
              title: "", width: 140, align: "right",
              render: (_, record) => (
                <span style={{ display: "flex", gap: 4, justifyContent: "flex-end" }}>
                  <Button size="small" onClick={() => openEditModal(record)}>Edit</Button>
                  {record._id !== userData?._id && (
                    <Button size="small" danger onClick={() => handleDelete(record._id)}>Delete</Button>
                  )}
                </span>
              ),
            },
          ]}
        />
      </DisplayComponent>

      <Modal
        title={editingUser ? "Edit user" : "New user"}
        open={isModalOpen}
        onCancel={() => setIsModalOpen(false)}
        onOk={handleSubmit}
        okText={editingUser ? "Save" : "Create user"}
        confirmLoading={createUser.isPending || updateUser.isPending}
      >
        <Form form={form} layout="vertical">
          <Form.Item name="name" label="Name" rules={[{ required: true }]}><Input /></Form.Item>
          <Form.Item name="username" label="Username" rules={[{ required: true, whitespace: true }]} extra="Used to sign in.">
            <Input autoComplete="off" />
          </Form.Item>
          <Form.Item
            name="authProvider"
            label="Sign-in method"
            extra={isMicrosoftUser ? "Signs in with their OSI Microsoft account. The email must match their Microsoft email." : undefined}
          >
            <Select
              disabled={!!editingUser}
              options={[{ label: "Local password", value: "local" }, { label: "Microsoft", value: "microsoft" }]}
            />
          </Form.Item>
          {!editingUser && !isMicrosoftUser && <Form.Item name="password" label="Password" rules={[{ required: true, min: 6 }]}><Input.Password /></Form.Item>}
          <Form.Item name="email" label="Email" rules={isMicrosoftUser ? [{ required: true, type: "email", message: "Enter their Microsoft email" }] : []}><Input /></Form.Item>
          <Form.Item name="role" label="Role" rules={[{ required: true }]}>
            <Select options={ROLES.map((r) => ({ label: ROLE_LABEL[r], value: r }))} disabled={editingUser?._id === userData?._id} />
          </Form.Item>
          {editingUser && (
            <Form.Item name="active" label="Active" valuePropName="checked">
              <Switch disabled={editingUser?._id === userData?._id} />
            </Form.Item>
          )}
          <Form.Item name="assignedProjectIds" label="Assigned projects">
            <Select mode="multiple" options={(projects || []).map((p: ProjectProps) => ({ label: p.name, value: p._id }))} />
          </Form.Item>
        </Form>
      </Modal>
    </PageShell>
  );
};
